import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { IndianRupee, RefreshCw, ChevronDown, ChevronUp, AlertTriangle, Zap } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const BREAKDOWN_LABELS = {
  freight: 'Freight', fuel_surcharge: 'Fuel', docket: 'Docket', ecc: 'ECC',
  owsp: 'OWSP', ras: 'RAS', idc: 'IDC', efss: 'EFSS', fov: 'FOV',
  cod_charge: 'COD', gst: 'GST',
};

const QuoteRow = ({ quote, cheapest }) => {
  const [open, setOpen] = useState(false);
  if (quote.error) {
    return (
      <div className="flex items-center justify-between px-3 py-2 rounded border border-wt-border/40 bg-wt-surface/30" data-testid={`ship-rate-error-${quote.integration_id}`}>
        <div className="min-w-0">
          <span className="text-sm text-wt-text-muted">{quote.name}</span>
          {quote.edl && <Badge className="ml-2 bg-orange-500/20 text-orange-400 border-orange-500/40 text-[10px]" title="Extended Delivery Location">EDL</Badge>}
          <span className="ml-2 text-xs text-red-400/80">{quote.error}</span>
        </div>
      </div>
    );
  }
  const bd = quote.breakdown || {};
  return (
    <div className={`rounded border ${cheapest ? 'border-emerald-500/50 bg-emerald-500/5' : 'border-wt-border/40 bg-wt-surface/30'}`} data-testid={`ship-rate-quote-${quote.integration_id}`}>
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-3 py-2 text-left" data-testid={`ship-rate-toggle-${quote.integration_id}`}>
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-medium text-wt-text-primary truncate">{quote.name}</span>
          {quote.via_courier && <span className="text-[10px] text-wt-text-muted truncate">via {quote.via_courier}</span>}
          {cheapest && <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/40 text-[10px]">Cheapest</Badge>}
          {(quote.source === 'delhivery-api' || quote.source === 'shiprocket-api') && <Zap className="w-3 h-3 text-amber-400" title="Live API rate" />}
          {quote.edl && <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/40 text-[10px]" title="Extended Delivery Location — delivery may take longer / cost extra">EDL</Badge>}
          {quote.zone && <span className="text-[10px] text-wt-text-muted">Zone {quote.zone}</span>}
          {quote.mode && <span className="text-[10px] text-wt-text-muted">{quote.mode}</span>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`text-sm font-semibold ${cheapest ? 'text-emerald-400' : 'text-wt-text-primary'}`}>{fmt(quote.total)}</span>
          {open ? <ChevronUp className="w-3 h-3 text-wt-text-muted" /> : <ChevronDown className="w-3 h-3 text-wt-text-muted" />}
        </div>
      </button>
      {open && (
        <div className="px-3 pb-2 grid grid-cols-3 gap-x-4 gap-y-0.5 text-xs text-wt-text-muted border-t border-wt-border/30 pt-2" data-testid={`ship-rate-breakdown-${quote.integration_id}`}>
          {Object.entries(BREAKDOWN_LABELS).map(([k, label]) =>
            bd[k] > 0 ? <div key={k} className="flex justify-between"><span>{label}</span><span>{fmt(bd[k])}</span></div> : null
          )}
          {bd.custom1?.amount > 0 && <div className="flex justify-between"><span>{bd.custom1.label || 'Custom 1'}</span><span>{fmt(bd.custom1.amount)}</span></div>}
          {bd.custom2?.amount > 0 && <div className="flex justify-between"><span>{bd.custom2.label || 'Custom 2'}</span><span>{fmt(bd.custom2.amount)}</span></div>}
          {quote.alternatives?.length > 0 && (
            <div className="col-span-3 border-t border-wt-border/30 mt-1 pt-1">
              <span className="text-wt-text-muted/70">Other Shiprocket options: </span>
              {quote.alternatives.map((a, ai) => (
                <span key={ai} className="mr-2">{a.courier_name} {fmt(a.rate)}{a.etd ? ` (ETA ${a.etd})` : ''}</span>
              ))}
            </div>
          )}
          <div className="flex justify-between col-span-3 border-t border-wt-border/30 mt-1 pt-1 text-wt-text-primary">
            <span>Chargeable wt: {quote.chargeable_weight_kg} kg{quote.volumetric_weight_kg ? ` (vol ${quote.volumetric_weight_kg} kg)` : ''} · {quote.source}</span>
            <span className="font-medium">{fmt(quote.total)}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export const ShippingRatesCompare = ({ order }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchRates = useCallback(async () => {
    if (!order?.id) return;
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.get(`${API_BASE_URL}/shipping-rates/order/${order.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(resp.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to fetch shipping rates');
    } finally {
      setLoading(false);
    }
  }, [order?.id]);

  useEffect(() => { fetchRates(); }, [fetchRates]);

  if (!order) return null;

  return (
    <div className="bg-wt-paper rounded-lg border border-wt-border p-4" data-testid="shipping-rates-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-wt-text-muted flex items-center gap-2">
          <IndianRupee className="w-4 h-4" />
          Shipping Rate Comparison
          {data?.to_pincode && (
            <span className="text-xs text-wt-text-muted/70">
              → {data.to_pincode}{data.dest_district ? `, ${data.dest_district}` : ''}{data.dest_state ? ` (${data.dest_state})` : ''}
            </span>
          )}
        </h3>
        <Button variant="ghost" size="sm" onClick={fetchRates} disabled={loading} className="h-7 px-2 text-wt-text-muted" data-testid="shipping-rates-refresh-btn">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {loading && !data && <p className="text-xs text-wt-text-muted animate-pulse" data-testid="shipping-rates-loading">Fetching rates from all couriers…</p>}
      {error && <p className="text-xs text-red-400" data-testid="shipping-rates-error">{error}</p>}
      {data?.error && <p className="text-xs text-amber-400 flex items-center gap-1" data-testid="shipping-rates-order-error"><AlertTriangle className="w-3 h-3" />{data.error}</p>}

      {data?.shipments?.map((sh, i) => (
        <div key={i} className={i > 0 ? 'mt-4' : ''} data-testid={`shipping-rates-shipment-${sh.shipment_number || 'full'}`}>
          {data.shipments.length > 1 && (
            <div className="text-xs font-medium text-wt-gold mb-1.5">
              Shipment {sh.shipment_number}{sh.booked ? ' · already booked' : ''}
            </div>
          )}
          <div className="text-[11px] text-wt-text-muted mb-1.5">
            Dead {sh.dead_weight_kg} kg · {sh.box_count} box{sh.box_count === 1 ? '' : 'es'} · value {fmt(sh.order_value)}
          </div>
          {sh.missing_dimensions?.length > 0 && (
            <p className="text-[11px] text-amber-400 mb-1.5 flex items-center gap-1" data-testid={`shipping-rates-missing-dims-${sh.shipment_number || 'full'}`}>
              <AlertTriangle className="w-3 h-3 shrink-0" />
              No package linked for: {sh.missing_dimensions.join(', ')} — rates exclude these items
            </p>
          )}
          {sh.error && <p className="text-xs text-amber-400">{sh.error}</p>}
          <div className="space-y-1.5">
            {(sh.quotes || []).map((q, qi) => (
              <QuoteRow key={q.integration_id} quote={q} cheapest={qi === 0 && q.total != null} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ShippingRatesCompare;
